import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, theme, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { AuthProvider } from './contexts/AuthContext';
import AuthenticatedLayout from './layouts/AuthenticatedLayout';
import ProtectedRoute from './components/ProtectedRoute';

import LoginPage                  from './pages/auth/LoginPage';
import DashboardPage              from './pages/dashboard/DashboardPage';
import InvoicesPage               from './pages/invoices/InvoicesPage';
import InvoiceDetailPage          from './pages/invoices/InvoiceDetailPage';
import EstimatesPage              from './pages/estimates/EstimatesPage';
import EstimateDetailPage         from './pages/estimates/EstimateDetailPage';
import ProfilePage                from './pages/profile/ProfilePage';
import WorkspacesPage             from './pages/workspaces/WorkspacesPage';
import WorkspaceDetailPage        from './pages/workspaces/WorkspaceDetailPage';

Spin.setDefaultIndicator(<LoadingOutlined style={{ fontSize: 22 }} spin />);

const antdTheme = {
  algorithm: theme.defaultAlgorithm,
  token: {
    colorPrimary:       '#2c7be5',
    colorBgContainer:   '#ffffff',
    colorBgLayout:      '#ffffff',
    colorBorder:        '#d8e2ef',
    borderRadius:       5,
    fontFamily:         "'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  components: {
    Layout: {
      headerPadding:  '0 24px',
      headerBg:       '#ffffff',
      siderBg:        '#ffffff',
    },
    Menu: {
      itemPaddingInline:  16,
      itemHeight:         42,
      itemHoverBg:        'var(--varexe-gray-200)',
      itemActiveBg:       'var(--varexe-soft-primary)',
      itemSelectedBg:     'var(--varexe-soft-primary)',
      itemSelectedColor:  'var(--varexe-primary)',
      groupTitleColor:    'var(--varexe-gray-500)',
      groupTitleFontSize: 11,
    },
    Table: {
      headerBorderRadius:   0,
      headerBg:             'transparent',
      footerBg:             'transparent',
      rowHoverBg:           'var(--varexe-gray-100)',
      borderColor:          'var(--varexe-border-color)',
      colorBgContainer:     'transparent',
    },
    Card: {
      borderRadiusLG: 8,
    },
  },
};

const ProtectedPage = ({ children }) => (
  <ProtectedRoute>
    <AuthenticatedLayout>
      {children}
    </AuthenticatedLayout>
  </ProtectedRoute>
);

function App() {
  return (
    <ConfigProvider theme={antdTheme}>
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public — org-scoped login hides Organization ID (share this URL with customers) */}
            <Route path="/:organizationPublicId/login" element={<LoginPage />} />
            <Route path="/login" element={<LoginPage />} />

            {/* Protected */}
            <Route path="/dashboard" element={<ProtectedPage><DashboardPage /></ProtectedPage>} />
            <Route path="/invoices"  element={<ProtectedPage><InvoicesPage /></ProtectedPage>} />
            <Route path="/invoices/:publicId" element={<ProtectedPage><InvoiceDetailPage /></ProtectedPage>} />
            <Route path="/estimates" element={<ProtectedPage><EstimatesPage /></ProtectedPage>} />
            <Route path="/estimates/:publicId" element={<ProtectedPage><EstimateDetailPage /></ProtectedPage>} />
            <Route path="/profile"   element={<ProtectedPage><ProfilePage /></ProtectedPage>} />
            <Route path="/workspaces" element={<ProtectedPage><WorkspacesPage /></ProtectedPage>} />
            <Route path="/workspaces/:documentType/:publicId" element={<ProtectedPage><WorkspaceDetailPage /></ProtectedPage>} />

            {/* Default */}
            <Route path="/"   element={<Navigate to="/dashboard" replace />} />
            <Route path="*"   element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ConfigProvider>
  );
}

export default App;
